import { BookOpen, Code2, PanelsTopLeft } from 'lucide-react';
import { useId, useRef } from 'react';

import { cn } from '@/shared/lib/cn';

export type WorkshopView = 'studio' | 'split' | 'code';

const VIEWS = [
  {
    id: 'studio',
    label: 'Studio',
    icon: BookOpen,
    description: 'Read instructions and review submissions',
  },
  { id: 'split', label: 'Split', icon: PanelsTopLeft, description: 'Read and code side by side' },
  { id: 'code', label: 'Code', icon: Code2, description: 'Focus on the coding environment' },
] as const;

export function WorkshopViewSwitcher({
  value,
  splitAvailable,
  onChange,
}: {
  value: WorkshopView;
  splitAvailable: boolean;
  onChange: (view: WorkshopView) => void;
}) {
  const id = useId();
  const group = useRef<HTMLDivElement>(null);
  const views = VIEWS.filter((view) => view.id !== 'split' || splitAvailable);
  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label="Workshop view"
      className="workshop-view-switcher"
    >
      {views.map((view, index) => (
        <button
          key={view.id}
          id={`${id}-${view.id}`}
          type="button"
          role="radio"
          data-workshop-view={view.id}
          aria-checked={value === view.id}
          aria-controls={
            view.id === 'studio'
              ? 'workshop-brief'
              : view.id === 'code'
                ? 'workshop-code-pane'
                : 'workshop-brief workshop-code-pane'
          }
          tabIndex={value === view.id ? 0 : -1}
          title={view.description}
          className={cn('workshop-view-option', value === view.id && 'workshop-view-option-active')}
          onClick={() => onChange(view.id)}
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const nextIndex =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? views.length - 1
                  : (index + (event.key === 'ArrowRight' ? 1 : -1) + views.length) % views.length;
            const next = views[nextIndex];
            if (!next) return;
            onChange(next.id);
            group.current?.querySelector<HTMLButtonElement>(`[id="${id}-${next.id}"]`)?.focus();
          }}
        >
          <view.icon className="size-3.5" aria-hidden="true" />
          {view.label}
        </button>
      ))}
    </div>
  );
}
